import React from 'react'
import classNames from 'classnames'

/** Object form for explicit x/y background positioning */
interface BackgroundPosition {
    x?: string
    y?: string
}

interface BackgroundProps {
    /** URL of the background image (required) */
    image: string
    /** Overlay class control — `true` adds `.overlay`, a string adds `.overlay-{overlay}` as well */
    overlay?: boolean | string
    /** Background position — a CSS string or an `{x, y}` object */
    position?: string | BackgroundPosition
    /** Array of video source URLs; types are inferred from file extensions */
    video?: string[]
    /** Extra CSS classes on the wrapper */
    className?: string
    /** Inline styles merged with the computed background-image rule */
    style?: React.CSSProperties
}

/**
 * Full-bleed background holder with optional overlay and looping video support.
 */
const Background = ({
    image,
    overlay,
    position,
    video,
    className,
    style,
}: BackgroundProps) => {
    const bgStyle: React.CSSProperties = { backgroundImage: `url(${image})`, ...style }

    if (typeof position === 'string') {
        bgStyle.backgroundPosition = position
    } else if (typeof position === 'object' && position !== null) {
        if (position.x) bgStyle.backgroundPositionX = position.x
        if (position.y) bgStyle.backgroundPositionY = position.y
    }

    return (
        <div
            className={classNames(
                'bg-holder',
                { overlay: overlay, [`overlay-${overlay}`]: typeof overlay === 'string' },
                className,
            )}
            style={bgStyle}
        >
            {video && (
                <video className='bg-video' autoPlay loop muted playsInline>
                    {video.map((src, index) => (
                        <source
                            key={index}
                            src={src}
                            type={`video/${src.split('.').pop()}`}
                        />
                    ))}
                </video>
            )}
        </div>
    )
}

export default Background
